import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface DenemeDersi {
  deneme_dersi_baslik: string | null;
  deneme_dersi_aciklama: string | null;
  deneme_dersi_tarih: string | null;
  deneme_dersi_saat: string | null;
  deneme_dersi_link: string | null;
}

const STEPS = [
  {
    icon: 'ri-user-add-line',
    title: 'Kayıtsız Katıl',
    desc: 'E-posta veya şifre gerekmeden, misafir olarak deneme dersine katılabilirsin.',
  },
  {
    icon: 'ri-live-line',
    title: 'Derse Bağlan',
    desc: 'Deneme dersi saatinde tek tıkla canlı derse bağlan, Zehra Hoca ile tanış.',
  },
  {
    icon: 'ri-vip-crown-line',
    title: 'Tam Erişime Geç',
    desc: 'Beğendiysen kayıt olarak ders programına, kayıtlara ve ödevlere tam erişim sağla.',
  },
];

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' });
}

export default function Misafir() {
  const [settings, setSettings] = useState<DenemeDersi | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from('zehrahoca_settings')
      .select('*')
      .eq('id', 1)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) {
          setSettings((data as DenemeDersi) ?? null);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const hasDers = Boolean(settings?.deneme_dersi_link);

  return (
    <div className="min-h-screen bg-background-50">
      {/* Üst bar */}
      <header className="sticky top-0 z-30 border-b border-white/10 bg-primary-950">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 md:px-6">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-500 font-heading text-lg font-extrabold text-primary-950">
              Z
            </div>
            <div className="leading-tight">
              <p className="font-heading text-[15px] font-bold text-white">Zehra Hocayla</p>
              <p className="text-xs font-medium text-accent-300">Matematik</p>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            <Link
              to="/giris"
              className="hidden whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold text-primary-100 transition hover:bg-white/10 hover:text-white sm:inline-flex"
            >
              Giriş Yap
            </Link>
            <Link
              to="/giris"
              className="whitespace-nowrap rounded-lg bg-accent-500 px-4 py-2 text-sm font-semibold text-primary-950 transition hover:bg-accent-400"
            >
              Kayıt Ol
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-950 via-primary-900 to-primary-800">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-accent-500/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-primary-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-4 py-16 text-center md:px-6 md:py-24">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/15 px-4 py-1.5 text-sm font-semibold text-accent-300 ring-1 ring-accent-500/30">
            <i className="ri-sparkling-2-line" />
            Misafir / Deneme Öğrencisi
          </span>
          <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-white md:text-5xl">
            Deneme Dersine<br />Hoş Geldin
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-primary-200">
            Kayıt olmadan Zehra Hoca ile bir deneme dersine katıl, öğretim tarzını gör ve matematikteki yolculuğuna nasıl başlayacağını keşfet.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            {hasDers && settings?.deneme_dersi_link ? (
              <a
                href={settings.deneme_dersi_link}
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap rounded-lg bg-accent-500 px-6 py-3 text-sm font-semibold text-primary-950 transition hover:bg-accent-400"
              >
                <i className="ri-vidicon-line mr-1.5" />
                Deneme Dersine Katıl
              </a>
            ) : (
              <span className="whitespace-nowrap rounded-lg bg-accent-500 px-6 py-3 text-sm font-semibold text-primary-950">
                Deneme Dersini Gör
              </span>
            )}
            <a
              href="#nasil"
              className="whitespace-nowrap rounded-lg border border-white/20 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
            >
              Nasıl Çalışır?
            </a>
          </div>
        </div>
      </section>

      {/* Deneme Dersi Kartı */}
      <section className="mx-auto max-w-4xl px-4 py-12 md:px-6 md:py-16">
        <div className="mb-6 text-center">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Sıradaki Deneme Dersin</h2>
          <p className="mt-1.5 text-sm text-foreground-600">Zehra Hoca'nın senin için planladığı deneme dersinin detayları.</p>
        </div>

        <div className="rounded-2xl border border-background-200 bg-white p-6 md:p-8">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-100 border-t-primary-500" />
            </div>
          ) : settings?.deneme_dersi_baslik || settings?.deneme_dersi_link ? (
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
                    <i className="ri-vidicon-line" />
                    Canlı Ders
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                    <i className="ri-user-heart-line" />
                    Ücretsiz Deneme
                  </span>
                </div>
                <h3 className="mt-4 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  {settings.deneme_dersi_baslik || 'Deneme Dersi'}
                </h3>
                {settings.deneme_dersi_aciklama && (
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{settings.deneme_dersi_aciklama}</p>
                )}
                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex items-center gap-3 rounded-lg bg-background-50 px-4 py-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                      <i className="ri-calendar-line" />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">Tarih</p>
                      <p className="text-sm font-semibold text-foreground-900">{formatDate(settings.deneme_dersi_tarih)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg bg-background-50 px-4 py-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                      <i className="ri-time-line" />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">Saat</p>
                      <p className="text-sm font-semibold text-foreground-900">{settings.deneme_dersi_saat || '—'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex shrink-0 flex-col items-stretch gap-3 lg:w-64">
                {hasDers && settings?.deneme_dersi_link ? (
                  <a
                    href={settings.deneme_dersi_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-primary-600"
                  >
                    <i className="ri-live-line text-base" />
                    Derse Katıl
                  </a>
                ) : (
                  <span className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-background-100 px-5 py-3 text-sm font-semibold text-foreground-500">
                    <i className="ri-time-line" />
                    Link yakında eklenecek
                  </span>
                )}
                <p className="text-center text-xs text-foreground-500">
                  Ders başlamadan 5 dakika önce tıklaman yeterli.
                </p>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-calendar-todo-line text-2xl" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">Henüz deneme dersi planlanmadı</h3>
              <p className="mx-auto mt-1.5 max-w-md text-sm text-foreground-600">
                Zehra Hoca deneme dersini planladığında burada görünecek. Takipte kal ya da Instagram üzerinden ulaş.
              </p>
              <a
                href="https://instagram.com/mathocaaofficall"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600"
              >
                <i className="ri-instagram-line text-base" />
                @mathocaaofficall
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Nasıl çalışır */}
      <section id="nasil" className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
          <div className="mb-8 text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Nasıl Çalışır?</h2>
            <p className="mt-1.5 text-sm text-foreground-600">Üç basit adımda deneme dersine katıl.</p>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative rounded-2xl border border-background-200 bg-white p-6">
                <span className="absolute right-5 top-5 font-heading text-4xl font-extrabold text-background-200">
                  {i + 1}
                </span>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-500 text-primary-950">
                  <i className={`${s.icon} text-lg`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kayıt CTA */}
      <section className="mx-auto max-w-4xl px-4 py-12 md:px-6 md:py-16">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-950 to-primary-800 p-8 text-center md:p-12">
          <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-accent-500/10 blur-3xl" />
          <div className="relative">
            <h2 className="font-heading text-2xl font-bold text-white md:text-3xl">Tam Erişim İçin Kayıt Ol</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-primary-200">
              Deneme dersini beğendiysen, kendi hesabını oluşturarak ders programına, ders kayıtlarına, dokümanlara ve ödevlere tam erişim sağla.
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/giris"
                className="whitespace-nowrap rounded-lg bg-accent-500 px-6 py-3 text-sm font-semibold text-primary-950 transition hover:bg-accent-400"
              >
                Hemen Kayıt Ol
              </Link>
              <Link
                to="/giris"
                className="whitespace-nowrap rounded-lg border border-white/20 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                Zaten Hesabım Var
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-background-200 bg-background-100">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 py-8 sm:flex-row md:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 font-heading text-base font-extrabold text-white">
              Z
            </div>
            <p className="text-sm font-semibold text-foreground-900">Zehra Hocayla Matematik</p>
          </div>
          <div className="flex items-center gap-4 text-sm text-foreground-600">
            <a
              href="https://instagram.com/mathocaaofficall"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 transition hover:text-foreground-900"
            >
              <i className="ri-instagram-line text-base" />
              @mathocaaofficall
            </a>
            <span className="hidden text-foreground-400 sm:inline">·</span>
            <span className="text-foreground-500">13 Yıllık Tecrübe</span>
          </div>
        </div>
      </footer>
    </div>
  );
}